import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, useSelectedDealer } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useBrandGroupsQuery } from "modules/BrandsDashboard/hooks";
import { getBrandGroupsByIds } from "modules/BrandsDashboard/utils";
import "modules/CheckInServices/CheckInServices.scss";
import { DealerServiceForm } from "modules/CheckInServices/components/DealerServiceModal/components";
import {
  useCreateCheckInServiceMutation,
  useDeleteCheckInServiceMutation,
  useUpdateCheckInServiceMutation
} from "modules/CheckInServices/components/DealerServiceModal/hooks";

import type { BrandGroupID, CheckInService } from "models";

export type DealerServiceFormValues = Omit<
  CheckInService,
  "id" | "dealer_id" | "created_on" | "updated_on" | "brand_groups" | "notification_email" | "send_to_lef" | "lef_lead_kind"
> & {
  brand_group_ids: BrandGroupID[];
};

type DealerServiceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  checkInService?: CheckInService | null;
};

const FormHelper = createFormHelper<DealerServiceFormValues>();

export const DealerServiceModal = ({ isOpen, onClose, checkInService }: DealerServiceModalProps) => {
  const { t } = useTranslation();

  const [hasErrors, setHasErrors] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isEditMode = Boolean(checkInService);
  const { selectedDealer } = useSelectedDealer();

  const { data: brandGroups = [] } = useBrandGroupsQuery();
  const { mutateAsync: createService, isPending: isCreatePending } = useCreateCheckInServiceMutation();
  const { mutateAsync: updateService, isPending: isUpdatePending } = useUpdateCheckInServiceMutation();
  const { mutateAsync: deleteService, isPending: isDeletePending } = useDeleteCheckInServiceMutation();

  const handleClose = () => {
    setHasErrors(false);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleSubmit = async (values: DealerServiceFormValues) => {
    if (!values.name || !values.description) {
      setHasErrors(true);
      return;
    }

    const { brand_group_ids, ...serviceValues } = values;
    const selectedBrandGroups = getBrandGroupsByIds(brand_group_ids, brandGroups);

    if (checkInService) await updateService({ ...checkInService, ...serviceValues, tag_id: values.tag_id || null, brand_groups: selectedBrandGroups });
    else
      await createService({
        ...serviceValues,
        id: -1,
        dealer_id: selectedDealer?.id ?? -1,
        created_on: new Date().toISOString(),
        updated_on: new Date().toISOString(),
        tag_id: values.tag_id || null,
        brand_groups: selectedBrandGroups
      });

    handleClose();
  };

  const handleDelete = async () => {
    if (!checkInService) return;
    await deleteService(checkInService.id);
    handleClose();
  };

  return (
    <>
      <Modal size="small" className="CheckInServiceModal" open={isOpen} onClose={handleClose} closeOnDimmerClick={false}>
        <Modal.Header>
          <h3>{isEditMode ? t("v8_edit_check_in_service", "Edit online check-in service") : t("v8_add_new_check_in_service", "Add new online check-in service")}</h3>
        </Modal.Header>

        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            name: checkInService?.name ?? "",
            description: checkInService?.description ?? "",
            info_url: checkInService?.info_url ?? "",
            price: checkInService?.price ?? 0,
            tag_id: checkInService?.tag_id ?? null,
            can_convert_to_intervention: checkInService?.can_convert_to_intervention ?? false,
            hide_for_lease_appointments: checkInService?.hide_for_lease_appointments ?? false,
            brand_group_ids: checkInService?.brand_groups?.map(brandGroup => brandGroup.id) ?? []
          }}
        >
          <Modal.Content>
            <DealerServiceForm hasErrors={hasErrors} />
          </Modal.Content>

          <Divider fitted />

          <Modal.Actions>
            <div className="actions-left">
              {isEditMode && checkInService && (
                <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.CheckInService}>
                  <Button type="button" color="red" onClick={() => setShowDeleteConfirm(true)} loading={isDeletePending}>
                    {t("v8_delete", "Delete")}
                    <Icon className="trash" />
                  </Button>
                </Can>
              )}
            </div>

            <div className="actions-right">
              <Button type="button" className="light" onClick={handleClose}>
                {t("v8_cancel", "Cancel")}
                <Icon className="xmark" />
              </Button>

              <Can I={[ACL_ACTIVITIES.Create, ACL_ACTIVITIES.Update]} the={ACL_RESOURCES.CheckInService}>
                <Button type="submit" color="green" disabled={isCreatePending || isUpdatePending} loading={isCreatePending || isUpdatePending}>
                  {isEditMode ? t("v8_save", "Save") : t("v8_create", "Create")}
                  <Icon className="check" />
                </Button>
              </Can>
            </div>
          </Modal.Actions>
        </FormHelper.Form>
      </Modal>

      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={showDeleteConfirm}
        handleCancel={() => setShowDeleteConfirm(false)}
        customTitle={t("v8_are_you_sure", "Are you sure?")}
        confirmMsg={t("v8_delete_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
        handleConfirm={handleDelete}
        isLoading={isDeletePending}
      />
    </>
  );
};
